<template>
  <loader :is-loading="isSubmitting" size="20" />
  <lf-dropdown-with-infinite-query
    ref="dropdownRef"
    :value="selectedUser"
    :name="`${$t('COMMON.SEARCH')} ${$t('COMMON.USER')}`"
    :search-term="`${$t('COMMON.USER')}`"
    :placeholder="placeholder"
    as-badge
    :show-warning-icon="false"
    :query-options="{
      queryKey: paginatedUsersQueryKey,
      queryFn: getPaginatedUsers,
    }"
    accessor="full_name"
    identifier="id"
    include-none
    :none-option-label="$t('COMMON.NONE')"
    class="assign-user-dropdown"
    @change="selectUser"
  >
    <template v-if="avatar" #toggle-button="{ toggle }">
      <lf-avatar
        v-if="selectedUser"
        :username="selectedUser"
        :size="32"
        :theme="TYPE_MAPPINGS[type].color"
        @click="toggle"
      />
      <span
        v-else
        class="cursor-pointer rounded-full px-2 py-2 hover:bg-gray-100"
        @click="toggle"
      >
        <icon-base :icon="TYPE_MAPPINGS[type].icon" />
      </span>
    </template>
  </lf-dropdown-with-infinite-query>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { booleanToBinary } from "@/helpers/common";
import {
  ANALYST,
  CLIENT_ANALYST,
  CLIENT_FUNDING_ADVISOR,
  CLIENT_UNDERWRITER,
  FUNDING_ADVISOR,
  UNDERWRITER,
  USER_BADGE_COLORS,
  USER_COLORS,
} from "@/helpers/constants";
import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import { useAuth } from "@/hooks/auth";
import usersApiService, {
  type GetUsersByRoleParams,
} from "@/services/modules/users";

import LfDropdownWithInfiniteQuery, {
  NONE_OPTION_ID,
} from "@/components/ui/inputs/LfDropdownWithInfiniteQuery.vue";

import type { AvatarTheme } from "@/components/ui/LFAvatar.vue";
import type { ISystemPreferences } from "@/models/applications";
import type { Role } from "@/models/options";
import type { AssignableUser, IUser } from "@/models/users";

const { t } = useI18n();

const dropdownRef = ref<InstanceType<
  typeof LfDropdownWithInfiniteQuery
> | null>(null);

const TYPE_TO_ROLE: Record<AssignableUser, Role> = {
  analyst: ANALYST,
  fundingAdvisor: FUNDING_ADVISOR,
  underwriter: UNDERWRITER,
  clientAnalyst: CLIENT_ANALYST,
  clientFundingAdvisor: CLIENT_FUNDING_ADVISOR,
  clientUnderwriter: CLIENT_UNDERWRITER,
};

const TYPE_MAPPINGS: Record<
  AssignableUser,
  {
    actionSelector: string;
    removeSelector: string;
    icon: string;
    color: AvatarTheme;
    isClientRole?: boolean;
  }
> = {
  underwriter: {
    actionSelector: "applications/assignUnderwriter",
    removeSelector: "applications/removeUnderwriter",
    icon: "briefcase-checked",
    color: USER_BADGE_COLORS[USER_COLORS.BLUE],
  },
  fundingAdvisor: {
    actionSelector: "applications/assignFundingAdvisor",
    removeSelector: "applications/removeFundingAdvisor",
    icon: "chat-bubble",
    color: USER_BADGE_COLORS[USER_COLORS.GREEN],
  },
  analyst: {
    actionSelector: "applications/assignAnalyst",
    removeSelector: "applications/removeAnalyst",
    icon: "pie-chart",
    color: USER_BADGE_COLORS[USER_COLORS.MINT],
  },
  clientUnderwriter: {
    actionSelector: "applications/assignClientUnderwriter",
    removeSelector: "applications/removeClientUnderwriter",
    icon: "briefcase-checked",
    color: USER_BADGE_COLORS[USER_COLORS.BLUE],
    isClientRole: true,
  },
  clientFundingAdvisor: {
    actionSelector: "applications/assignClientFundingAdvisor",
    removeSelector: "applications/removeClientFundingAdvisor",
    icon: "chat-bubble",
    color: USER_BADGE_COLORS[USER_COLORS.GREEN],
    isClientRole: true,
  },
  clientAnalyst: {
    actionSelector: "applications/assignClientAnalyst",
    removeSelector: "applications/removeClientAnalyst",
    icon: "pie-chart",
    color: USER_BADGE_COLORS[USER_COLORS.MINT],
    isClientRole: true,
  },
};

const {
  appId,
  clientId,
  modelValue = "",
  type = "fundingAdvisor",
  avatar = false,
  isExtended = false,
} = defineProps<{
  modelValue?: string | number;
  appId: string;
  type?: AssignableUser;
  clientId?: string | number;
  avatar?: boolean;
  isExtended?: boolean;
}>();

const emit = defineEmits<{
  (eventName: "changed", userId: string | number): void;
}>();

const { isFundingAdvisor, isClientFundingAdvisor } = useAuth();
const store = useStore();

const isSubmitting = ref(false);
const selectedUser = ref(modelValue);

const placeholder = computed(() => (isExtended ? t("COMMON.UNASSIGNED") : "-"));

const getPaginatedUsers = (
  params: Record<string, string | string[] | number | number[]>
) => {
  const serviceParams: GetUsersByRoleParams = {
    role: TYPE_TO_ROLE[type],
    ignore_assigned_deals: booleanToBinary(true),
    page: params.page as number | undefined,
    search: params.search as string | undefined,
  };
  if (clientId && TYPE_MAPPINGS[type].isClientRole) {
    serviceParams.client_id = String(clientId);
  }
  return usersApiService.getUsersByRole(serviceParams);
};

const paginatedUsersQueryKey = computed(() =>
  QUERY_KEYS.USERS.byRole(TYPE_TO_ROLE[type], {
    ignoreAssignedDeals: true,
    ...(clientId && TYPE_MAPPINGS[type].isClientRole && { clientId }),
  })
);

const dealVisibilityPreferences = computed<ISystemPreferences>(
  () => store.getters["applications/systemPreferences"]
);

const currentUser = computed<IUser>(() => store.getters["auth/user"]);

async function selectUser(selectedValue: IUser | undefined) {
  const isRemoval =
    !selectedValue || String(selectedValue.id) === NONE_OPTION_ID;

  isSubmitting.value = true;
  const previousUser = selectedUser.value;
  selectedUser.value = isRemoval ? "" : selectedValue.full_name || "";

  const payload = {
    appId: appId,
    userId: isRemoval ? "remove" : selectedValue.id,
    removeDeal: false,
    user: isRemoval ? undefined : selectedValue,
    ...(clientId && { clientId }),
  };

  if (
    type === "fundingAdvisor" &&
    (isFundingAdvisor || isClientFundingAdvisor) &&
    !dealVisibilityPreferences.value.deal_visibility["funding advisor"]
      ?.assigned &&
    !isRemoval &&
    Number(selectedValue.id) !== currentUser.value.id
  ) {
    payload.removeDeal = true;
  }

  const action = !isRemoval
    ? TYPE_MAPPINGS[type].actionSelector
    : TYPE_MAPPINGS[type].removeSelector;
  const actionPayload = !isRemoval ? payload : payload.appId;
  try {
    await store.dispatch(action, actionPayload);
    store.dispatch("applications/syncActiveToAll");
    emit("changed", isRemoval ? "remove" : selectedValue.id);
  } catch {
    selectedUser.value = previousUser;
  } finally {
    isSubmitting.value = false;
  }
}

watch(
  () => modelValue,
  (value) => {
    selectedUser.value = value;
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.assign-user-dropdown:deep() {
  .badge {
    @apply h-6 text-xxs leading-4 font-semibold uppercase;
  }
}
</style>
